import { zodResolver } from '@hookform/resolvers/zod';
import {
  useIntegrationMqttGetAllBrokers,
  useIntegrationMqttCreateBroker,
  useIntegrationMqttUpdateBroker,
  useIntegrationMqttDeleteBroker,
  useIntegrationMqttPauseBroker,
  useIntegrationMqttResumeBroker,
  useIntegrationMqttGetAllInputMappings,
  useIntegrationMqttDeleteInputMapping,
  useGetAllMqttDevices,
  useDeleteMqttDevice,
  useIntegrationMqttGetAllSensors,
  useIntegrationMqttDeleteSensor,
  getIntegrationMqttGetAllBrokersQueryKey,
  getIntegrationMqttGetAllInputMappingsQueryKey,
  getGetAllMqttDevicesQueryKey,
  getIntegrationMqttGetAllSensorsQueryKey,
  type MqttBroker,
  type MqttDevice,
  type MqttInputMapping,
  type MqttSensor,
  type MqttConnectionState,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Plus,
  Server,
  Cpu,
  Activity,
  Zap,
  Pencil,
  Trash2,
  Pause,
  Play,
  Monitor,
  Copy,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { MqttInputPanel, MqttSensorPanel, MqttDevicePanel } from './components';

import { Button, Modal, TextField } from '@/components/ui';

// Broker schemas
const createBrokerSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  host: z.string().min(1, 'Host is required').max(255),
  port: z.coerce.number().min(1, 'Port must be 1-65535').max(65535, 'Port must be 1-65535'),
  secure: z.boolean().optional(),
  username: z.string().max(255).optional(),
  password: z.string().max(255).optional(),
});

const updateBrokerSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255).optional(),
  host: z.string().min(1, 'Host is required').max(255).optional(),
  port: z.coerce.number().min(1).max(65535).optional(),
  secure: z.boolean().optional(),
  username: z.string().max(255).optional(),
  password: z.string().max(255).optional(),
});

type CreateBrokerFormData = z.infer<typeof createBrokerSchema>;
type UpdateBrokerFormData = z.infer<typeof updateBrokerSchema>;

type Tab = 'brokers' | 'devices' | 'sensors' | 'inputs';

export function MqttSettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('brokers');

  return (
    <div className="space-y-8">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              MQTT Integration
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Manage MQTT brokers, devices, sensors, and inputs
            </p>
          </div>
          <Link to={ROUTES.MQTT_MONITOR}>
            <Button variant="secondary" leftIcon={<Monitor className="h-4 w-4" />}>
              Monitor
            </Button>
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-white/[0.06]">
        <nav className="-mb-px flex gap-6">
          {(['brokers', 'devices', 'sensors', 'inputs'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'brokers' && <BrokersTab />}
      {activeTab === 'devices' && <DevicesTab />}
      {activeTab === 'sensors' && <SensorsTab />}
      {activeTab === 'inputs' && <InputsTab />}
    </div>
  );
}

// ============================================================================
// BROKERS TAB
// ============================================================================

function BrokersTab() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useIntegrationMqttGetAllBrokers();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBroker, setEditingBroker] = useState<MqttBroker | null>(null);
  const [deletingBroker, setDeletingBroker] = useState<MqttBroker | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const createMutation = useIntegrationMqttCreateBroker();
  const updateMutation = useIntegrationMqttUpdateBroker();
  const deleteMutation = useIntegrationMqttDeleteBroker();
  const pauseMutation = useIntegrationMqttPauseBroker();
  const resumeMutation = useIntegrationMqttResumeBroker();

  const brokers = data?.brokers ?? [];

  const invalidateBrokers = () => {
    queryClient.invalidateQueries({ queryKey: getIntegrationMqttGetAllBrokersQueryKey() });
  };

  const handlePauseResume = async (broker: MqttBroker) => {
    if (!broker.id) return;
    try {
      const isPaused = broker.connection?.state === 'not-connected';
      if (isPaused) {
        await resumeMutation.mutateAsync({ id: broker.id });
      } else {
        await pauseMutation.mutateAsync({ id: broker.id });
      }
      invalidateBrokers();
    } catch {
      // Error handling done by mutation
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Brokers
        </h2>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddModal(true)}
        >
          Add Broker
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch brokers</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your MQTT brokers.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Broker List */}
      {!isLoading && !isError && (
        <>
          {brokers.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Server className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No brokers configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                Add your first MQTT broker to get started
              </p>
              <Button
                className="mt-6"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setShowAddModal(true)}
              >
                Add Broker
              </Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              {brokers.map((broker, index) => (
                <BrokerListItem
                  key={broker.id}
                  broker={broker}
                  isFirst={index === 0}
                  onEdit={() => setEditingBroker(broker)}
                  onDelete={() => setDeletingBroker(broker)}
                  onPauseResume={() => handlePauseResume(broker)}
                  isPauseLoading={
                    (pauseMutation.isPending || resumeMutation.isPending) &&
                    (pauseMutation.variables?.id === broker.id ||
                      resumeMutation.variables?.id === broker.id)
                  }
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Add Broker Modal */}
      <AddBrokerModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddError(null);
        }}
        onSubmit={async (data) => {
          setAddError(null);
          try {
            await createMutation.mutateAsync({ data });
            setShowAddModal(false);
            invalidateBrokers();
          } catch (err) {
            const error = err as { message?: string };
            if (error.message?.includes('already exists')) {
              setAddError('A broker with this host and port combination already exists.');
            } else {
              setAddError('Failed to add broker. Please try again.');
            }
          }
        }}
        isLoading={createMutation.isPending}
        error={addError}
      />

      {/* Edit Broker Modal */}
      {editingBroker && (
        <EditBrokerModal
          isOpen={true}
          broker={editingBroker}
          onClose={() => {
            setEditingBroker(null);
            setEditError(null);
          }}
          onSubmit={async (data) => {
            if (!editingBroker.id) return;
            setEditError(null);
            try {
              await updateMutation.mutateAsync({
                id: editingBroker.id,
                data,
              });
              setEditingBroker(null);
              invalidateBrokers();
            } catch (err) {
              const error = err as { message?: string };
              if (error.message?.includes('already exists')) {
                setEditError('A broker with this host and port combination already exists.');
              } else {
                setEditError('Failed to update broker. Please try again.');
              }
            }
          }}
          isLoading={updateMutation.isPending}
          error={editError}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingBroker && (
        <DeleteBrokerModal
          isOpen={true}
          broker={deletingBroker}
          onClose={() => setDeletingBroker(null)}
          onConfirm={async () => {
            if (!deletingBroker.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingBroker.id });
              setDeletingBroker(null);
              invalidateBrokers();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </>
  );
}

interface BrokerListItemProps {
  broker: MqttBroker;
  isFirst: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onPauseResume: () => void;
  isPauseLoading: boolean;
}

function BrokerListItem({
  broker,
  isFirst,
  onEdit,
  onDelete,
  onPauseResume,
  isPauseLoading,
}: BrokerListItemProps) {
  const connectionState = broker.connection?.state as MqttConnectionState | undefined;

  const getStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'bg-emerald-400 shadow-lg shadow-emerald-400/30';
      case 'connecting':
        return 'bg-amber-400 shadow-lg shadow-amber-400/30';
      case 'not-connected':
        return 'bg-gray-400 dark:bg-zinc-600';
      default:
        return 'bg-gray-400 dark:bg-zinc-600';
    }
  };

  const getStatusText = () => {
    switch (connectionState) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'not-connected':
        return 'Disconnected';
      default:
        return 'Unknown';
    }
  };

  const isPaused = connectionState === 'not-connected';

  return (
    <div
      className={`flex items-center justify-between p-4 ${
        !isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`h-3 w-3 rounded-full ${getStatusColor()}`} />
        <div>
          <div className="text-[15px] font-medium text-gray-900 dark:text-white">{broker.name}</div>
          <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
            <span>
              {broker.host}:{broker.port}
            </span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span>{getStatusText()}</span>
            {broker.secure && (
              <>
                <span className="text-gray-300 dark:text-zinc-700">•</span>
                <span className="text-emerald-500">TLS</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPauseResume}
          disabled={isPauseLoading}
          title={isPaused ? 'Resume connection' : 'Pause connection'}
        >
          {isPauseLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          ) : isPaused ? (
            <Play className="h-4 w-4" />
          ) : (
            <Pause className="h-4 w-4" />
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// DEVICES TAB
// ============================================================================

function DevicesTab() {
  const queryClient = useQueryClient();
  const { data: brokersData } = useIntegrationMqttGetAllBrokers();
  const { data, isLoading, isError, refetch } = useGetAllMqttDevices();

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingDevice, setEditingDevice] = useState<MqttDevice | null>(null);
  const [duplicatingDevice, setDuplicatingDevice] = useState<MqttDevice | null>(null);
  const [deletingDevice, setDeletingDevice] = useState<MqttDevice | null>(null);

  const deleteMutation = useDeleteMqttDevice();

  const devices = data?.devices ?? [];
  const brokers = brokersData?.brokers ?? [];

  const invalidateDevices = () => {
    queryClient.invalidateQueries({ queryKey: getGetAllMqttDevicesQueryKey() });
  };

  const getBrokerName = (brokerId?: string) => {
    const broker = brokers.find((b) => b.id === brokerId);
    return broker?.name ?? 'Unknown Broker';
  };

  const handleClosePanel = () => {
    setShowAddPanel(false);
    setEditingDevice(null);
    setDuplicatingDevice(null);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Devices
        </h2>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddPanel(true)}
          disabled={brokers.length === 0}
          title={brokers.length === 0 ? 'Add a broker first' : undefined}
        >
          Add Device
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch devices</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your MQTT devices.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Device List */}
      {!isLoading && !isError && (
        <>
          {devices.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Cpu className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No devices configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                {brokers.length === 0
                  ? 'Add a broker first, then add devices'
                  : 'Add your first MQTT device'}
              </p>
              {brokers.length > 0 && (
                <Button
                  className="mt-6"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowAddPanel(true)}
                >
                  Add Device
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              {devices.map((device, index) => (
                <DeviceListItem
                  key={device.id}
                  device={device}
                  brokerName={getBrokerName(device.brokerId)}
                  isFirst={index === 0}
                  onEdit={() => setEditingDevice(device)}
                  onDuplicate={() => setDuplicatingDevice(device)}
                  onDelete={() => setDeletingDevice(device)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Device Panel (Add/Edit/Duplicate) */}
      <MqttDevicePanel
        isOpen={showAddPanel || !!editingDevice || !!duplicatingDevice}
        onClose={handleClosePanel}
        device={editingDevice}
        duplicateFrom={duplicatingDevice}
        brokers={brokers}
        onSuccess={invalidateDevices}
      />

      {/* Delete Confirmation Modal */}
      {deletingDevice && (
        <DeleteDeviceModal
          isOpen={true}
          device={deletingDevice}
          onClose={() => setDeletingDevice(null)}
          onConfirm={async () => {
            if (!deletingDevice.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingDevice.id });
              setDeletingDevice(null);
              invalidateDevices();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </>
  );
}

interface DeviceListItemProps {
  device: MqttDevice;
  brokerName: string;
  isFirst: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function DeviceListItem({
  device,
  brokerName,
  isFirst,
  onEdit,
  onDuplicate,
  onDelete,
}: DeviceListItemProps) {
  return (
    <div
      className={`flex items-center justify-between p-4 ${
        !isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-zinc-800">
          <Cpu className="h-5 w-5 text-gray-500 dark:text-zinc-400" />
        </div>
        <div>
          <div className="text-[15px] font-medium text-gray-900 dark:text-white">{device.name}</div>
          <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
            <span className="capitalize">{device.deviceType}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span>{brokerName}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span className="font-mono text-xs">{device.commandTopic}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onDuplicate} title="Duplicate">
          <Copy className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit} title="Edit">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} title="Delete">
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// SENSORS TAB
// ============================================================================

function SensorsTab() {
  const queryClient = useQueryClient();
  const { data: brokersData } = useIntegrationMqttGetAllBrokers();
  const { data, isLoading, isError, refetch } = useIntegrationMqttGetAllSensors();

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingSensor, setEditingSensor] = useState<MqttSensor | null>(null);
  const [duplicatingSensor, setDuplicatingSensor] = useState<MqttSensor | null>(null);
  const [deletingSensor, setDeletingSensor] = useState<MqttSensor | null>(null);

  const deleteMutation = useIntegrationMqttDeleteSensor();

  const sensors = data?.sensors ?? [];
  const brokers = brokersData?.brokers ?? [];

  const invalidateSensors = () => {
    queryClient.invalidateQueries({ queryKey: getIntegrationMqttGetAllSensorsQueryKey() });
  };

  const getBrokerName = (brokerId?: string) => {
    const broker = brokers.find((b) => b.id === brokerId);
    return broker?.name ?? 'Unknown Broker';
  };

  const handleClosePanel = () => {
    setShowAddPanel(false);
    setEditingSensor(null);
    setDuplicatingSensor(null);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Sensors
        </h2>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddPanel(true)}
          disabled={brokers.length === 0}
          title={brokers.length === 0 ? 'Add a broker first' : undefined}
        >
          Add Sensor
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch sensors</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your MQTT sensors.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Sensor List */}
      {!isLoading && !isError && (
        <>
          {sensors.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Activity className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No sensors configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                {brokers.length === 0
                  ? 'Add a broker first, then add sensors'
                  : 'Add your first MQTT sensor'}
              </p>
              {brokers.length > 0 && (
                <Button
                  className="mt-6"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowAddPanel(true)}
                >
                  Add Sensor
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              {sensors.map((sensor, index) => (
                <SensorListItem
                  key={sensor.id}
                  sensor={sensor}
                  brokerName={getBrokerName(sensor.brokerId)}
                  isFirst={index === 0}
                  onEdit={() => setEditingSensor(sensor)}
                  onDuplicate={() => setDuplicatingSensor(sensor)}
                  onDelete={() => setDeletingSensor(sensor)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Sensor Panel (Add/Edit/Duplicate) */}
      <MqttSensorPanel
        isOpen={showAddPanel || !!editingSensor || !!duplicatingSensor}
        onClose={handleClosePanel}
        sensor={editingSensor}
        duplicateFrom={duplicatingSensor}
        brokers={brokers}
        onSuccess={invalidateSensors}
      />

      {/* Delete Confirmation Modal */}
      {deletingSensor && (
        <DeleteSensorModal
          isOpen={true}
          sensor={deletingSensor}
          onClose={() => setDeletingSensor(null)}
          onConfirm={async () => {
            if (!deletingSensor.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingSensor.id });
              setDeletingSensor(null);
              invalidateSensors();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </>
  );
}

interface SensorListItemProps {
  sensor: MqttSensor;
  brokerName: string;
  isFirst: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function SensorListItem({
  sensor,
  brokerName,
  isFirst,
  onEdit,
  onDuplicate,
  onDelete,
}: SensorListItemProps) {
  return (
    <div
      className={`flex items-center justify-between p-4 ${
        !isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-zinc-800">
          <Activity className="h-5 w-5 text-gray-500 dark:text-zinc-400" />
        </div>
        <div>
          <div className="text-[15px] font-medium text-gray-900 dark:text-white">{sensor.name}</div>
          <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
            <span className="font-mono text-xs">{sensor.id}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span>{brokerName}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span className="font-mono text-xs">{sensor.topic}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onDuplicate} title="Duplicate">
          <Copy className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit} title="Edit">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} title="Delete">
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// INPUT MAPPINGS TAB
// ============================================================================

function InputsTab() {
  const queryClient = useQueryClient();
  const { data: brokersData } = useIntegrationMqttGetAllBrokers();
  const { data, isLoading, isError, refetch } = useIntegrationMqttGetAllInputMappings();

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingMapping, setEditingMapping] = useState<MqttInputMapping | null>(null);
  const [duplicatingMapping, setDuplicatingMapping] = useState<MqttInputMapping | null>(null);
  const [deletingMapping, setDeletingMapping] = useState<MqttInputMapping | null>(null);

  const deleteMutation = useIntegrationMqttDeleteInputMapping();

  const mappings = data?.inputMappings ?? [];
  const brokers = brokersData?.brokers ?? [];

  const invalidateMappings = () => {
    queryClient.invalidateQueries({ queryKey: getIntegrationMqttGetAllInputMappingsQueryKey() });
  };

  const getBrokerName = (brokerId?: string) => {
    const broker = brokers.find((b) => b.id === brokerId);
    return broker?.name ?? 'Unknown Broker';
  };

  const handleClosePanel = () => {
    setShowAddPanel(false);
    setEditingMapping(null);
    setDuplicatingMapping(null);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Inputs
        </h2>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddPanel(true)}
          disabled={brokers.length === 0}
          title={brokers.length === 0 ? 'Add a broker first' : undefined}
        >
          Add Input Mapping
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch input mappings</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your MQTT input mappings.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          {mappings.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Zap className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No input mappings configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                {brokers.length === 0
                  ? 'Add a broker first, then add input mappings'
                  : 'Add your first MQTT input mapping'}
              </p>
              {brokers.length > 0 && (
                <Button
                  className="mt-6"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowAddPanel(true)}
                >
                  Add Input Mapping
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              {mappings.map((mapping, index) => (
                <InputMappingListItem
                  key={mapping.id}
                  mapping={mapping}
                  brokerName={getBrokerName(mapping.brokerId)}
                  isFirst={index === 0}
                  onEdit={() => setEditingMapping(mapping)}
                  onDuplicate={() => setDuplicatingMapping(mapping)}
                  onDelete={() => setDeletingMapping(mapping)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <MqttInputPanel
        isOpen={showAddPanel || !!editingMapping || !!duplicatingMapping}
        onClose={handleClosePanel}
        inputMapping={editingMapping}
        duplicateFrom={duplicatingMapping}
        brokers={brokers}
        onSuccess={invalidateMappings}
      />

      {deletingMapping && (
        <DeleteInputMappingModal
          isOpen={true}
          mapping={deletingMapping}
          onClose={() => setDeletingMapping(null)}
          onConfirm={async () => {
            if (!deletingMapping.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingMapping.id });
              setDeletingMapping(null);
              invalidateMappings();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </>
  );
}

interface InputMappingListItemProps {
  mapping: MqttInputMapping;
  brokerName: string;
  isFirst: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function InputMappingListItem({
  mapping,
  brokerName,
  isFirst,
  onEdit,
  onDuplicate,
  onDelete,
}: InputMappingListItemProps) {
  const title = mapping.name?.trim() || mapping.inputId || 'Input mapping';

  return (
    <div
      className={`flex items-center justify-between p-4 ${
        !isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-zinc-800">
          <Zap className="h-5 w-5 text-gray-500 dark:text-zinc-400" />
        </div>
        <div>
          <div className="text-[15px] font-medium text-gray-900 dark:text-white">{title}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
            <span className="font-mono text-xs">{mapping.inputId}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span>{brokerName}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span className="font-mono text-xs">{mapping.topic}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onDuplicate} title="Duplicate">
          <Copy className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit} title="Edit">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} title="Delete">
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// MODALS
// ============================================================================

// Broker Modals

interface AddBrokerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBrokerFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function AddBrokerModal({ isOpen, onClose, onSubmit, isLoading, error }: AddBrokerModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateBrokerFormData>({
    resolver: zodResolver(createBrokerSchema),
    defaultValues: {
      port: 1883,
      secure: false,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="add-broker-form" loading={isLoading}>
        Add Broker
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add MQTT Broker"
      subtitle="Connect to a new MQTT message broker"
      size="md"
      footer={footerContent}
    >
      <form id="add-broker-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Home Broker"
          error={errors.name?.message}
          {...register('name')}
        />

        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <TextField
              label="Host"
              placeholder="mqtt.example.com"
              error={errors.host?.message}
              {...register('host')}
            />
          </div>
          <TextField
            label="Port"
            type="number"
            placeholder="1883"
            error={errors.port?.message}
            {...register('port')}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="secure"
            className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900"
            {...register('secure')}
          />
          <label htmlFor="secure" className="text-sm text-gray-700 dark:text-zinc-300">
            Use TLS/SSL
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Username"
            placeholder="Optional"
            error={errors.username?.message}
            {...register('username')}
          />
          <TextField
            label="Password"
            type="password"
            placeholder="Optional"
            error={errors.password?.message}
            {...register('password')}
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface EditBrokerModalProps {
  isOpen: boolean;
  broker: MqttBroker;
  onClose: () => void;
  onSubmit: (data: UpdateBrokerFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function EditBrokerModal({
  isOpen,
  broker,
  onClose,
  onSubmit,
  isLoading,
  error,
}: EditBrokerModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateBrokerFormData>({
    resolver: zodResolver(updateBrokerSchema),
    defaultValues: {
      name: broker.name ?? '',
      host: broker.host ?? '',
      port: broker.port ?? 1883,
      secure: broker.secure ?? false,
      username: broker.username ?? '',
      password: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="edit-broker-form" loading={isLoading}>
        Save Changes
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Broker"
      subtitle="Update broker connection settings"
      size="md"
      footer={footerContent}
    >
      <form id="edit-broker-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Home Broker"
          error={errors.name?.message}
          {...register('name')}
        />

        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <TextField
              label="Host"
              placeholder="mqtt.example.com"
              error={errors.host?.message}
              {...register('host')}
            />
          </div>
          <TextField
            label="Port"
            type="number"
            placeholder="1883"
            error={errors.port?.message}
            {...register('port')}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="secure-edit"
            className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900"
            {...register('secure')}
          />
          <label htmlFor="secure-edit" className="text-sm text-gray-700 dark:text-zinc-300">
            Use TLS/SSL
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Username"
            placeholder="Optional"
            error={errors.username?.message}
            {...register('username')}
          />
          <TextField
            label="Password"
            type="password"
            placeholder="Leave blank to keep current"
            error={errors.password?.message}
            {...register('password')}
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface DeleteBrokerModalProps {
  isOpen: boolean;
  broker: MqttBroker;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteBrokerModal({
  isOpen,
  broker,
  onClose,
  onConfirm,
  isLoading,
}: DeleteBrokerModalProps) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Broker"
      subtitle="This action cannot be undone"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete{' '}
        <strong className="text-gray-900 dark:text-white">{broker.name}</strong>? This will also
        remove all associated devices and sensors.
      </p>
    </Modal>
  );
}

// Device Modals

interface DeleteDeviceModalProps {
  isOpen: boolean;
  device: MqttDevice;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteDeviceModal({
  isOpen,
  device,
  onClose,
  onConfirm,
  isLoading,
}: DeleteDeviceModalProps) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Device"
      subtitle="This action cannot be undone"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete{' '}
        <strong className="text-gray-900 dark:text-white">{device.name}</strong>?
      </p>
    </Modal>
  );
}

// Sensor Modals

interface DeleteSensorModalProps {
  isOpen: boolean;
  sensor: MqttSensor;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteSensorModal({
  isOpen,
  sensor,
  onClose,
  onConfirm,
  isLoading,
}: DeleteSensorModalProps) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Sensor"
      subtitle="This action cannot be undone"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete{' '}
        <strong className="text-gray-900 dark:text-white">{sensor.name}</strong>?
      </p>
    </Modal>
  );
}

interface DeleteInputMappingModalProps {
  isOpen: boolean;
  mapping: MqttInputMapping;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteInputMappingModal({
  isOpen,
  mapping,
  onClose,
  onConfirm,
  isLoading,
}: DeleteInputMappingModalProps) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete
      </Button>
    </>
  );

  const title = mapping.name?.trim() || mapping.id || 'this mapping';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Input Mapping"
      subtitle="This action cannot be undone"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete{' '}
        <strong className="text-gray-900 dark:text-white">{title}</strong>?
      </p>
    </Modal>
  );
}
